import { createElement, useLayoutEffect, useRef, type CSSProperties, type ReactNode } from "react";
import type { BgSpot } from "@bing/engine-backgammon";
import { prefersReducedMotion } from "./card-motion";

/* ------------------------------------------------------------------ *
 * Backgammon board motions — the pure, testable half of the
 * BackgammonTable "exclamations" (design spec §12 / the pulled
 * 07-motion-interaction-spec): the checker SLIDE (from→to) and the
 * dice TUMBLE. Mirrors chess's `chess-motion.ts` (WAAPI in a layout
 * effect, `fill:both`, the fill dropped on finish so no lingering
 * transform traps `position:fixed` descendants — the recurring
 * `view-enter` bug), so a happy-dom / SSR runtime (no `Element.animate`)
 * places every chip at rest with nothing broken.
 *
 * The ONE difference from chess: a chess board is a uniform 8×8 grid so
 * the slide is `translate(dx*100%, …)` of a square-filling wrapper — no
 * measurement. A backgammon board's points are NOT a uniform grid (six
 * per quadrant, a central bar, an outer tray), so the slide is a
 * MEASURED pixel delta between two spot centres (`BgLayout.spotCenter`,
 * injected by the table once its real geometry lands).
 *
 * `prefersReducedMotion` is reused from `card-motion` — ONE source of
 * truth for the OS "reduce motion" preference across every table.
 * ------------------------------------------------------------------ */

export { prefersReducedMotion };

/**
 * One checker's WALK from source to destination.
 *
 * 340ms read as a teleport (operator: "the motion of moving checkers is too fast"), then 560ms was
 * still brisk next to the «deliberate» tempo. 700ms is a step you can actually follow.
 * **LOCKSTEP** with `WALK_MS` in game/backgammon-replay.ts, which budgets the time for it.
 */
export const WALK_MS = 700;
export const SLIDE_EASING = "cubic-bezier(.2,.7,.3,1)";

/** Measured board metrics: the pixel centre of a spot (a point index, the bar, or the tray). */
export interface BgLayout {
  spotCenter(spot: BgSpot): { x: number; y: number } | null;
}

/** The pixel delta a checker starts at so it appears to come FROM `from` and land on `to`. */
export function checkerSlideDelta(
  from: BgSpot,
  to: BgSpot,
  layout: BgLayout,
): { dx: number; dy: number } {
  const a = layout.spotCenter(from);
  const b = layout.spotCenter(to);
  if (!a || !b) return { dx: 0, dy: 0 };
  return { dx: a.x - b.x, dy: a.y - b.y };
}

function slideKeyframes(dx: number, dy: number): Keyframe[] {
  return [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "none" }];
}

/**
 * A chip that slides in from its origin (§12 A6/A8/A11/A12). Clone of chess's `SlidePiece`: WAAPI in a
 * layout effect, `fill:both`, fill dropped on finish (avoids the `view-enter` trap), mount-once (key it
 * by the move so a fresh move remounts). `animate === false`, a zero delta, reduced motion, or no
 * `Element.animate` all place the chip at rest. `data-slide-from` is the one observable flight marker.
 */
export function SlideChecker({
  dx,
  dy,
  animate,
  className,
  style,
  children,
}: {
  dx: number;
  dy: number;
  animate: boolean;
  className?: string;
  style?: CSSProperties;
  children: ReactNode;
}): ReactNode {
  const ref = useRef<HTMLSpanElement | null>(null);
  const flying = animate && (dx !== 0 || dy !== 0) && !prefersReducedMotion();
  useLayoutEffect(() => {
    if (!flying) return undefined;
    const el = ref.current;
    if (!el || typeof el.animate !== "function") return undefined;
    const anim = el.animate(slideKeyframes(dx, dy), {
      duration: WALK_MS,
      easing: SLIDE_EASING,
      fill: "both",
    });
    anim.onfinish = (): void => anim.cancel();
    return () => anim.cancel();
    // Mount-once (deliberately empty deps), like chess's `SlidePiece`: a chip's slide plays
    // exactly when its node appears; callers key this wrapper by the move so a fresh move
    // remounts it. Re-running would re-fly a settled chip on any unrelated re-render (a clock tick).
  }, []);
  return createElement(
    "span",
    { ref, className, style, ...(flying ? { "data-slide-from": `${dx},${dy}` } : {}) },
    children,
  );
}

/* The dice motion lives in `backgammon-dice.tsx` now — a real CSS 3D cube. The flat-span tumble
   that used to sit here had no `perspective` ancestor, so its rotateX/rotateY just squashed the
   die. Do not reintroduce a 2-D "tumble". */
